import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Ban, Pencil, PlusCircle, RotateCw } from "lucide-react";
import dayjs from "dayjs";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, Card, Button, Alert, Badge, Stat, Modal, Input, DatePicker, Table, DescriptionList, EmptyState, FormStack, InlineLoading, ResponsiveGrid, Text, type Column } from "@marekl/adminer/components";
import {
  useCancelRide,
  useCancelVoucher,
  useExtendVoucherValidity,
  useRegisterRide,
  useUpdateVoucherContacts,
  useVoucherDetail,
  type RideResponse,
  type VoucherResponse,
  type VoucherState,
} from "../../lib/queries/vouchers";
import { ApiError } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import {
  formatDate,
  formatDateTime,
  formatRelative,
  pluralizeCzech,
} from "@marekl/adminer/format";
import { useAuth } from "../../auth/useAuth";
import { extractVoucherId } from "../../lib/barcode";
import styles from "./VoucherDetailPage.module.css";

const STATE_LABEL: Record<VoucherState, string> = {
  active: "Aktivní",
  depleted: "Vyčerpán",
  expired: "Expirován",
  cancelled: "Stornován",
};

const STATE_VARIANT: Record<VoucherState, "ok" | "warn" | "err" | "muted"> = {
  active: "ok",
  depleted: "muted",
  expired: "err",
  cancelled: "warn",
};

export function VoucherDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "superadmin";

  const detail = useVoucherDetail(id);
  const registerRide = useRegisterRide(id ?? "");
  const cancelRide = useCancelRide();
  const cancelVoucher = useCancelVoucher(id ?? "");
  const updateContacts = useUpdateVoucherContacts(id ?? "");
  const extendValidity = useExtendVoucherValidity(id ?? "");

  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);
  const [pendingClaimCount, setPendingClaimCount] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<RideResponse | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);

  const [cancelVoucherOpen, setCancelVoucherOpen] = useState(false);
  const [cancelVoucherReason, setCancelVoucherReason] = useState("");
  const [cancelVoucherError, setCancelVoucherError] = useState<string | null>(
    null,
  );

  const [editContactsOpen, setEditContactsOpen] = useState(false);
  const [contactsForm, setContactsForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    note: "",
  });
  const [contactsError, setContactsError] = useState<string | null>(null);

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDate, setExtendDate] = useState<dayjs.Dayjs | null>(null);
  const [extendReason, setExtendReason] = useState("");
  const [extendError, setExtendError] = useState<string | null>(null);

  const secondCrumb = detail.isLoading ? "…"
    : !detail.data ? "Nenalezeno"
    : detail.data.voucher.barcode;
  useSetTopbarCrumbs([{ label: "Vouchery", href: "/vouchers" }, { label: secondCrumb }]);

  if (detail.isLoading) {
    return (
      <>
        <Page.Content>
          <InlineLoading>Načítáme detail voucheru…</InlineLoading>
        </Page.Content>
      </>
    );
  }

  if (detail.isError || !detail.data) {
    const isNotFound =
      detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <>
        <Page.Content>
          <Alert variant="err">
            {isNotFound
              ? "Voucher s tímto identifikátorem neexistuje."
              : errorMessage(
                  detail.error,
                  "Detail voucheru se nepodařilo načíst.",
                )}
          </Alert>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate("/vouchers")}>
              Zpět na vyhledávání
            </Button>
          </div>
        </Page.Content>
      </>
    );
  }

  const v: VoucherResponse = detail.data.voucher;
  const rides = detail.data.rides;
  const eshopCert = detail.data.eshop_certificate;
  const activeRides = rides.filter((r) => !r.cancelled);

  const validUntilRelative = formatRelative(v.valid_until);
  const canRegister = v.state === "active" && v.rides_remaining > 0;

  async function handleRegisterRide(count = 1) {
    setRegisterError(null);
    setRegisterSuccess(null);
    if (count > 1) {
      setPendingClaimCount(count);
      return;
    }
    await runRegisterRide(count);
  }

  async function runRegisterRide(count: number) {
    setPendingClaimCount(null);
    try {
      const res = await registerRide.mutateAsync({ method: "manual", count });
      setRegisterSuccess(
        count > 1
          ? `Zaregistrováno ${count} jízd. Zbývá ${res.voucher.rides_remaining} z ${res.voucher.rides_total}.`
          : `Jízda zaregistrována. Zbývá ${res.voucher.rides_remaining} z ${res.voucher.rides_total}.`,
      );
    } catch (err) {
      setRegisterError(
        errorMessage(err, "Jízdu se nepodařilo zaregistrovat."),
      );
    }
  }

  function openCancel(ride: RideResponse) {
    setCancelTarget(ride);
    setCancelReason("");
    setCancelError(null);
  }

  function closeCancel() {
    setCancelTarget(null);
    setCancelReason("");
    setCancelError(null);
  }

  async function handleConfirmCancel() {
    if (!cancelTarget) return;
    if (cancelReason.trim().length < 3) {
      setCancelError("Uveďte prosím důvod (min. 3 znaky).");
      return;
    }
    setCancelError(null);
    try {
      await cancelRide.mutateAsync({
        rideID: cancelTarget.id,
        voucherID: v.id,
        reason: cancelReason.trim(),
      });
      closeCancel();
    } catch (err) {
      setCancelError(errorMessage(err, "Storno se nezdařilo."));
    }
  }

  function openCancelVoucher() {
    setCancelVoucherOpen(true);
    setCancelVoucherReason("");
    setCancelVoucherError(null);
  }

  function closeCancelVoucher() {
    setCancelVoucherOpen(false);
    setCancelVoucherReason("");
    setCancelVoucherError(null);
  }

  async function handleConfirmCancelVoucher() {
    if (cancelVoucherReason.trim().length < 3) {
      setCancelVoucherError("Uveďte prosím důvod (min. 3 znaky).");
      return;
    }
    setCancelVoucherError(null);
    try {
      await cancelVoucher.mutateAsync({ reason: cancelVoucherReason.trim() });
      closeCancelVoucher();
    } catch (err) {
      setCancelVoucherError(
        errorMessage(err, "Voucher se nepodařilo stornovat."),
      );
    }
  }

  function openEditContacts() {
    setContactsForm({
      customer_name: v.customer_name ?? "",
      customer_phone: v.customer_phone ?? "",
      customer_email: v.customer_email ?? "",
      note: v.note ?? "",
    });
    setContactsError(null);
    setEditContactsOpen(true);
  }

  function closeEditContacts() {
    setEditContactsOpen(false);
    setContactsError(null);
  }

  async function handleConfirmEditContacts() {
    setContactsError(null);
    const phone = contactsForm.customer_phone.trim();
    const email = contactsForm.customer_email.trim();
    if (!phone && !email) {
      setContactsError("Uveďte alespoň jeden kontakt — telefon nebo e-mail.");
      return;
    }
    try {
      await updateContacts.mutateAsync({
        customer_name: contactsForm.customer_name.trim(),
        customer_phone: phone === "" ? null : phone,
        customer_email: email === "" ? null : email,
        note: contactsForm.note.trim() === "" ? null : contactsForm.note.trim(),
      });
      closeEditContacts();
    } catch (err) {
      setContactsError(errorMessage(err, "Kontakt se nepodařilo uložit."));
    }
  }

  function openExtend() {
    const minNext = dayjs(v.valid_until).add(1, "day").startOf("day");
    const suggested = dayjs().add(1, "month").startOf("day");
    setExtendDate(suggested.isAfter(minNext) ? suggested : minNext);
    setExtendReason("");
    setExtendError(null);
    setExtendOpen(true);
  }

  function closeExtend() {
    setExtendOpen(false);
    setExtendError(null);
  }

  async function handleConfirmExtend() {
    setExtendError(null);
    if (!extendDate || !extendDate.isValid()) {
      setExtendError("Vyberte nový datum platnosti.");
      return;
    }
    if (!extendDate.isAfter(dayjs(v.valid_until))) {
      setExtendError(
        "Nový datum musí být později než stávající datum platnosti.",
      );
      return;
    }
    if (extendReason.trim().length < 3) {
      setExtendError("Uveďte prosím důvod (min. 3 znaky).");
      return;
    }
    try {
      await extendValidity.mutateAsync({
        valid_until: extendDate.endOf("day").toISOString(),
        reason: extendReason.trim(),
      });
      closeExtend();
    } catch (err) {
      setExtendError(
        errorMessage(err, "Platnost se nepodařilo prodloužit."),
      );
    }
  }

  const rideColumns: Column<RideResponse>[] = [
    {
      key: "time",
      header: "Datum a čas",
      primary: true,
      render: (r) => <Text variant="mono">{formatDateTime(r.created_at)}</Text>,
    },
    {
      key: "actor",
      header: "Recepční",
      render: () => "Recepce",
    },
    {
      key: "action",
      header: "Akce",
      render: (r) => (
        <span>
          {r.method === "manual"
            ? "Registrace jízdy (ručně)"
            : "Registrace jízdy"}
          {r.cancelled && (
            <Badge variant="err" className={styles.badgeInline}>
              Stornováno
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (r) => {
        if (r.cancelled) {
          return (
            <Table.Actions>
              <Text variant="muted">—</Text>
            </Table.Actions>
          );
        }
        const sameDay = isSameLocalDay(r.created_at);
        const allowed = isManager || sameDay;
        if (!allowed) {
          return (
            <Table.Actions>
              <Text
                variant="muted"
                title="Storno je možné pouze v den vytvoření; po půlnoci může stornovat jen manažer."
              >
                —
              </Text>
            </Table.Actions>
          );
        }
        return (
          <Table.Actions>
            <Table.Action variant="danger" onClick={() => openCancel(r)}>
              Storno
            </Table.Action>
          </Table.Actions>
        );
      },
    },
  ];

  return (
    <>
      <Page.Content stack>
        <Page.Header
          title={
            <>
              Voucher{" "}
              {extractVoucherId(v.barcode) ? (
                <>
                  <span className={styles.barcode}>{extractVoucherId(v.barcode)}</span>
                  {" "}
                  <span className={styles.barcodeSecondary}>({v.barcode})</span>
                </>
              ) : (
                <span className={styles.barcode}>{v.barcode}</span>
              )}
              {" "}
              <Badge variant={STATE_VARIANT[v.state]}>
                {STATE_LABEL[v.state]}
              </Badge>
            </>
          }
          subtitle={
            <>
              {v.voucher_type?.name ?? "—"} · vydáno {formatDate(v.created_at)}{" "}
              · platnost do {formatDate(v.valid_until)}
            </>
          }
          subtitleVariant="default"
          actions={
            <>
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleRegisterRide(1)}
                loading={registerRide.isPending}
                disabled={!canRegister}
                title={
                  !canRegister
                    ? "Voucher není aktivní nebo nemá zbývající jízdy."
                    : undefined
                }
              >
                <PlusCircle size={16} /> Zaregistrovat jízdu
              </Button>
            </>
          }
        />

        {registerError && (
          <div className={styles.headerAlert}>
            <Alert variant="err">{registerError}</Alert>
          </div>
        )}
        {registerSuccess && (
          <div className={styles.headerAlert}>
            <Alert variant="ok">{registerSuccess}</Alert>
          </div>
        )}

        {v.rides_total > 0 && (
          <Card
            header={
              <Card.Header
                title="Rychlá registrace jízd"
                subtitle={`${v.rides_remaining} z ${v.rides_total} zbývá`}
                subtitlePlacement="right"
              />
            }
          >
            <div className={styles.claimGrid}>
              {Array.from({ length: v.rides_total }, (_, idx) => {
                const boxNumber = idx + 1;
                const isAvailable = boxNumber <= v.rides_remaining;
                const claimCount = boxNumber;
                const isClickable = isAvailable && canRegister && !registerRide.isPending;
                const claimedNumber = v.rides_total - boxNumber + 1;
                return (
                  <button
                    key={boxNumber}
                    type="button"
                    className={
                      !isAvailable
                        ? `${styles.claimBox} ${styles.claimBoxClaimed}`
                        : !canRegister
                          ? `${styles.claimBox} ${styles.claimBoxDisabled}`
                          : styles.claimBox
                    }
                    onClick={() => isClickable && handleRegisterRide(claimCount)}
                    disabled={!isClickable}
                    title={
                      !isAvailable
                        ? `Jízda ${claimedNumber} už proběhla`
                        : claimCount === 1
                          ? "Zaregistrovat 1 jízdu"
                          : claimCount >= 2 && claimCount <= 4
                            ? `Zaregistrovat ${claimCount} jízdy najednou`
                            : `Zaregistrovat ${claimCount} jízd najednou`
                    }
                  >
                    {boxNumber}
                  </button>
                );
              })}
            </div>
            <p className={styles.claimHint}>
              Klikněte na číslo pro registraci dané počtu jízd najednou. Šedé boxy
              označují již proběhlé jízdy.
            </p>
          </Card>
        )}

        <div className={styles.statsGrid}>
          <Stat
            label="Zbývá jízd"
            value={`${v.rides_remaining} / ${v.rides_total}`}
          />
          <Stat
            label="Platnost do"
            value={formatDate(v.valid_until)}
            delta={validUntilRelative}
          />
          <Stat
            label="Zdroj"
            value={
              v.source === "eshop" ? (
                eshopCert ? (
                  <Link
                    to={`/certificates?code=${encodeURIComponent(eshopCert.redemption_code)}`}
                  >
                    E-shop
                  </Link>
                ) : (
                  "E-shop"
                )
              ) : (
                "Recepce"
              )
            }
          />
        </div>

        <ResponsiveGrid>
          <Card header={<Card.Header title="Kontaktní údaje" />}>
            <DescriptionList>
              <DescriptionList.Item label="Jméno">
                {v.customer_name || <Text variant="muted">—</Text>}
              </DescriptionList.Item>
              <DescriptionList.Item label="Telefon">
                {v.customer_phone || <Text variant="muted">—</Text>}
              </DescriptionList.Item>
              <DescriptionList.Item label="E-mail">
                {v.customer_email || <Text variant="muted">—</Text>}
              </DescriptionList.Item>
              <DescriptionList.Item label="Poznámka">
                {v.note || <Text variant="muted">—</Text>}
              </DescriptionList.Item>
            </DescriptionList>
          </Card>

          <Card
            header={
              <Card.Header title="Akce správce" subtitlePlacement="right" />
            }
          >
            <div className={styles.actionStack}>
              <Button
                block
                onClick={openExtend}
                disabled={!isManager || v.state === "cancelled"}
                title={
                  !isManager
                    ? "Pouze manažer nebo superadmin"
                    : v.state === "cancelled"
                      ? "Stornovaný voucher nelze prodloužit"
                      : undefined
                }
              >
                <RotateCw size={14} /> Prodloužit platnost
              </Button>
              <Button
                block
                onClick={openEditContacts}
                disabled={!isManager}
                title={!isManager ? "Pouze manažer nebo superadmin" : undefined}
              >
                <Pencil size={14} /> Opravit kontakt
              </Button>
              <Button
                block
                variant="danger"
                onClick={openCancelVoucher}
                disabled={
                  !isManager || v.state === "cancelled" || v.state === "expired"
                }
                title={
                  !isManager
                    ? "Pouze manažer nebo superadmin"
                    : v.state === "cancelled"
                      ? "Voucher je již stornován"
                      : v.state === "expired"
                        ? "Expirovaný voucher již nelze stornovat"
                        : undefined
                }
              >
                <Ban size={14} /> Stornovat voucher
              </Button>
            </div>
          </Card>
        </ResponsiveGrid>

        <Page.SectionHeader
          title="Historie jízd"
          meta={`${rides.length} ${pluralizeCzech(rides.length, ["záznam", "záznamy", "záznamů"])} · ${activeRides.length} platných`}
        />

        <Table
          cards
          columns={rideColumns}
          rows={rides}
          rowKey={(r) => r.id}
          empty={
            <EmptyState title="U tohoto voucheru zatím nejsou žádné jízdy." />
          }
        />
      </Page.Content>

      <Modal
        open={!!cancelTarget}
        title="Stornovat jízdu"
        onClose={closeCancel}
        footer={
          <>
            <Button onClick={closeCancel} disabled={cancelRide.isPending}>
              Zrušit
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmCancel}
              loading={cancelRide.isPending}
            >
              Stornovat jízdu
            </Button>
          </>
        }
      >
        {cancelTarget && (
          <div>
            <p className={styles.modalLead}>
              Jízda z {formatDateTime(cancelTarget.created_at)} bude označena
              jako stornovaná a vrácena do zůstatku voucheru.
            </p>
            <Input
              label="Důvod storna"
              placeholder="např. omylem dvojitý sken"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              error={cancelError ?? undefined}
              autoFocus
            />
          </div>
        )}
      </Modal>

      <Modal
        open={cancelVoucherOpen}
        title="Stornovat voucher"
        onClose={closeCancelVoucher}
        footer={
          <>
            <Button
              onClick={closeCancelVoucher}
              disabled={cancelVoucher.isPending}
            >
              Zrušit
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmCancelVoucher}
              loading={cancelVoucher.isPending}
            >
              Stornovat voucher
            </Button>
          </>
        }
      >
        <div>
          <p className={styles.modalLead}>
            Voucher bude označen jako stornovaný a jeho čárový kód se vrátí jako
            neplatný do banky kódů. Akci nelze vrátit.
          </p>
          <Input
            label="Důvod stornování"
            placeholder="např. zákazník odstoupil od smlouvy"
            value={cancelVoucherReason}
            onChange={(e) => setCancelVoucherReason(e.target.value)}
            error={cancelVoucherError ?? undefined}
            autoFocus
          />
        </div>
      </Modal>

      <Modal
        open={editContactsOpen}
        title="Opravit kontakt"
        onClose={closeEditContacts}
        footer={
          <>
            <Button
              onClick={closeEditContacts}
              disabled={updateContacts.isPending}
            >
              Zrušit
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmEditContacts}
              loading={updateContacts.isPending}
            >
              Uložit změny
            </Button>
          </>
        }
      >
        <FormStack>
          <Input
            label="Jméno zákazníka"
            value={contactsForm.customer_name}
            onChange={(e) =>
              setContactsForm((s) => ({ ...s, customer_name: e.target.value }))
            }
          />
          <Input
            label="Telefon"
            value={contactsForm.customer_phone}
            onChange={(e) =>
              setContactsForm((s) => ({ ...s, customer_phone: e.target.value }))
            }
          />
          <Input
            label="E-mail"
            value={contactsForm.customer_email}
            onChange={(e) =>
              setContactsForm((s) => ({ ...s, customer_email: e.target.value }))
            }
          />
          <Input
            label="Poznámka"
            value={contactsForm.note}
            onChange={(e) =>
              setContactsForm((s) => ({ ...s, note: e.target.value }))
            }
          />
          {contactsError && <Alert variant="err">{contactsError}</Alert>}
        </FormStack>
      </Modal>

      <Modal
        open={extendOpen}
        title="Prodloužit platnost"
        onClose={closeExtend}
        footer={
          <>
            <Button onClick={closeExtend} disabled={extendValidity.isPending}>
              Zrušit
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmExtend}
              loading={extendValidity.isPending}
            >
              Prodloužit
            </Button>
          </>
        }
      >
        <FormStack>
          <p className={styles.modalLead}>
            Stávající datum platnosti:{" "}
            <strong>{formatDate(v.valid_until)}</strong>. Nový datum musí být
            později.
          </p>
          <DatePicker
            label="Nové datum platnosti"
            value={extendDate}
            onChange={(d) => setExtendDate(d)}
            minDate={dayjs(v.valid_until).add(1, "day")}
          />
          <Input
            label="Důvod prodloužení"
            placeholder="např. zákazník nestihl využít z důvodu nemoci"
            value={extendReason}
            onChange={(e) => setExtendReason(e.target.value)}
          />
          {extendError && <Alert variant="err">{extendError}</Alert>}
        </FormStack>
      </Modal>

      <Modal
        open={pendingClaimCount !== null}
        title="Zaregistrovat více jízd najednou"
        onClose={() => setPendingClaimCount(null)}
        footer={
          <>
            <Button
              onClick={() => setPendingClaimCount(null)}
              disabled={registerRide.isPending}
            >
              Zrušit
            </Button>
            <Button
              variant="primary"
              onClick={() => pendingClaimCount && runRegisterRide(pendingClaimCount)}
              loading={registerRide.isPending}
            >
              Zaregistrovat {pendingClaimCount ?? 0}{" "}
              {pendingClaimCount === 1
                ? "jízdu"
                : pendingClaimCount && pendingClaimCount >= 2 && pendingClaimCount <= 4
                  ? "jízdy"
                  : "jízd"}
            </Button>
          </>
        }
      >
        {pendingClaimCount !== null && (
          <p className={styles.modalLead}>
            Opravdu zaregistrovat <strong>{pendingClaimCount}</strong>{" "}
            {pendingClaimCount === 1
              ? "jízdu"
              : pendingClaimCount >= 2 && pendingClaimCount <= 4
                ? "jízdy"
                : "jízd"}{" "}
            najednou? Tato akce odečte {pendingClaimCount} ze zbývajícího počtu
            jízd voucheru.
          </p>
        )}
      </Modal>
    </>
  );
}

function isSameLocalDay(value: unknown): boolean {
  const d = dayjs.isDayjs(value) ? value : dayjs(value as string | Date);
  if (!d.isValid()) return false;
  return d.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
}
